import { CloseOutlineIcon, RightOutlineIcon } from '@carboncopyco/icons';
import type { Workspace } from '@carboncopyco/types';
import {
  Button,
  ColorHex,
  Form,
  InputTextField,
  LoadingDots,
  Spacer,
  TextSubHeader,
  Tooltip,
} from '@carboncopyco/ui';
import { MMMDoYYYY } from '@carboncopyco/utils';
import { type RefObject, forwardRef, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { object, string } from 'yup';
import { useQuery } from '../../hooks';
import type { WorkspaceSelectionField } from '../../types/dependencies';
import {
  FinishCapturingAction,
  useFinishCapturingActionsContext,
} from './FinishCapturingActionsContext';
import { useTourCapturePageProps } from './TourCapturePageContext';

const validationSchema = object({
  tourName: string().required('Required'),
  description: string(),
  workspaceId: string(),
}).required();

interface IFormProps {
  tourName: string;
  description: string;
  workspaceId: number;
  submitAction: FinishCapturingAction;
}

interface ISaveTourCaptureTooltip {
  workspaces: Workspace[];
  workspaceId: string;
  isLoading: boolean;
  tourName: string;
  onClose: () => void;
  onSubmit: (values: IFormProps) => void;
  WorkspaceSelectionField: WorkspaceSelectionField;
  open: boolean;
}

export const SaveTourCaptureTooltip = forwardRef(
  function SaveTourCaptureTooltip(
    {
      workspaces,
      workspaceId,
      isLoading,
      tourName,
      onSubmit,
      onClose,
      WorkspaceSelectionField,
      open,
    }: ISaveTourCaptureTooltip,
    anchorRef: RefObject<HTMLElement>
  ) {
    const { desktopHooks } = useTourCapturePageProps();
    const { setTargetWorkspaceId, setTourSubmitAction } =
      useFinishCapturingActionsContext();
    desktopHooks?.useFocusModalAppService(open);
    const query = useQuery();
    const { state } = useLocation();
    const { folderId: folderIdFromParams } = useParams<{
      folderId?: string;
    }>();

    const tourOrigin =
      tourName.toLocaleLowerCase() === 'untitled tour'
        ? query.get('appUrl')
        : tourName;
    const initialValues = useMemo(
      () => ({
        tourName,
        description: `Tour based on ${tourOrigin}\n${MMMDoYYYY(Date.now())}`,
        workspaceId: workspaceId ? Number(workspaceId) : undefined,
        submitAction: FinishCapturingAction.EDIT,
      }),
      [workspaceId, tourName, tourOrigin]
    );

    const folderId = state?.folderId ?? folderIdFromParams;

    return (
      <>
        {open && (
          <Tooltip
            visible={true}
            anchorRef={anchorRef}
            noArrow
            placement="bottom-end"
            shadow="md"
            interactive
          >
            <div className="p-5">
              {isLoading ? (
                <div className="justify-center flex w-ds-sm-modal h-ds-sm-modal">
                  <LoadingDots color={ColorHex.natural600} />
                </div>
              ) : (
                <div className="w-ds-400">
                  <Form<IFormProps>
                    enableReinitialize
                    onSubmit={(values) => {
                      setTargetWorkspaceId(String(values.workspaceId));
                      onSubmit(values);
                    }}
                    validationSchema={validationSchema}
                    initialValues={initialValues}
                  >
                    <>
                      <div className="flex items-center gap-2.5">
                        <TextSubHeader
                          color="text-ds-natural-600"
                          label="Finish capturing"
                        />
                        <div
                          onClick={onClose}
                          className="cursor-pointer ml-auto"
                        >
                          <CloseOutlineIcon />
                        </div>
                      </div>
                      <Spacer amount={3} />
                      <InputTextField
                        name="tourName"
                        label="Tour name"
                        required
                      />
                      <InputTextField
                        label="Describe your changes"
                        name="description"
                        multiline
                        inputSize="sm"
                      />
                      {workspaces?.length > 0 && !folderId && (
                        <>
                          <Spacer />
                          <WorkspaceSelectionField
                            workspaces={workspaces}
                            label="Save to"
                            name="workspaceId"
                            width={400}
                          />
                        </>
                      )}
                      <Spacer amount={4} />
                      <div className="w-full flex justify-end gap-2 ">
                        <Button
                          onClick={() =>
                            setTourSubmitAction(FinishCapturingAction.EDIT)
                          }
                          name="edit"
                          label="Continue to edit"
                          rightIcon={<RightOutlineIcon />}
                          submit
                        />
                      </div>
                    </>
                  </Form>
                </div>
              )}
            </div>
          </Tooltip>
        )}
      </>
    );
  }
);
